import React, { useEffect, useState } from 'react';
import {
    TableHead, TableRow, TableCell, IconButton, TableBody, Collapse, Menu, MenuItem, Box
} from '@mui/material';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import { useAppDispatch, useAppSelector } from '../../app/hooks';
import { OrganizationMemberModel } from '../../typings';
import { DeleteConfirmationDialog, DialogTable, DialogTableTitle } from '@lp/lp-ui';
import { NewOrganizationMemberModel } from '../../functions';
import { userDataSelected } from '../../app/userSlice';
import { useCurrentUser } from '../../app/useCurrentUser';
import OrganizationPanel from './OrganizationPanel';

export default function NewUserOrganizationsPanel() {
    const dispatch = useAppDispatch();
    const { data } = useAppSelector(store => store.user);
    const currentUser = useAppSelector(store => store.currentUser);
    const [organizationData, setOrganizationData] = useState<OrganizationMemberModel>(NewOrganizationMemberModel());
    const [newEditOpen, setNewEditOpen] = useState(false);
    const [deleteOrganizationDialogOpen, setDeleteOrganizationDialogOpen] = useState(false);
    const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
    const menuOpen = Boolean(anchorEl);
    const [isAdmin, isTenantAdmin] = useCurrentUser();

    useEffect(() => {
        if (!currentUser.data?.isTenantAdmin) {
            const organizationMember:OrganizationMemberModel = data!.userOrganizations.length > 0
            ? data!.userOrganizations[0]
            : {
                organizationId: currentUser.data?.organization.id ?? 0,
                organizationName: currentUser.data?.organization.name ?? "",
                organizationDisplayName: currentUser.data?.organization.displayName ?? "",
                roles: []
            };
            dispatch(userDataSelected({ ...data!, userOrganizations: [organizationMember] }));
            setOrganizationData(organizationMember);
            setNewEditOpen(true);
        }
    }, []);

    const handleOrganizationPanelClose = () => {
        setNewEditOpen(false);
        setAnchorEl(null);
    };

    const handleMenuOpenClick = (event: React.MouseEvent<HTMLButtonElement>, unit: OrganizationMemberModel) => {
        setAnchorEl(event.currentTarget);
        setOrganizationData(unit);
    };

    const handleOrganizationDeleteOk = () => {
        handleMenuClose();
        if (organizationData && data !== null) {
            const index = data.userOrganizations.indexOf(organizationData);
            if (index !== undefined && index > -1) {
                const userOrganizationsClone = Object.assign([], data.userOrganizations);
                userOrganizationsClone.splice(index, 1);
                dispatch(userDataSelected({ ...data, userOrganizations: userOrganizationsClone }));
            }
        }
        setOrganizationData(NewOrganizationMemberModel());
        setDeleteOrganizationDialogOpen(false);
    }

    const handleMenuClose = () => {
        setAnchorEl(null);
    };

    return (
        <React.Fragment>
            <Box mx={-5}>
                <DialogTableTitle title="Organizations">
                    <React.Fragment>
                        {!newEditOpen && isAdmin &&
                            <IconButton size="large"
                                onClick={() => { setOrganizationData(NewOrganizationMemberModel()); setNewEditOpen(true); }}
                            >
                                <AddCircleOutlineIcon />
                            </IconButton>
                        }
                    </React.Fragment>
                </DialogTableTitle>

                <Collapse in={newEditOpen} sx={{ border: '1px solid lightgrey' }}>
                    <OrganizationPanel
                        data={organizationData}
                        handleClose={handleOrganizationPanelClose}
                    />
                </Collapse>
                <Collapse in={!newEditOpen}>
                    {data?.userOrganizations !== undefined && data?.userOrganizations.length > 0 &&
                        <DialogTable>
                            <TableHead>
                                <TableRow>
                                    <TableCell>Organization</TableCell>
                                    <TableCell>Organization Roles</TableCell>
                                    <TableCell></TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {data?.userOrganizations.map((row, i) => (
                                    <TableRow key={i}>
                                        <TableCell>
                                            {row.organizationDisplayName}
                                        </TableCell>
                                        <TableCell>
                                            {row.roles.map(r => r.name).join(", ")}
                                        </TableCell>
                                        <TableCell align="right">
                                            <IconButton
                                                aria-label="delete"
                                                size="small"
                                                color="primary"
                                                onClick={(e) => handleMenuOpenClick(e, row)}
                                            >
                                                <MoreVertIcon />
                                            </IconButton>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </DialogTable>
                    }
                </Collapse>
            </Box>

            <DeleteConfirmationDialog
                title="Are you sure you&apos;d like to delete this Organization?"
                open={deleteOrganizationDialogOpen}
                handleClose={() => { setDeleteOrganizationDialogOpen(false); setAnchorEl(null); }}
                handleSave={handleOrganizationDeleteOk}
            />

            <Menu
                id="basic-menu"
                anchorEl={anchorEl}
                open={menuOpen}
                onClose={handleMenuClose}
                MenuListProps={{
                    'aria-labelledby': 'basic-button',
                }}
            >
                <MenuItem onClick={() => { setNewEditOpen(true); handleMenuClose(); }}>Edit</MenuItem>
                <MenuItem onClick={() => setDeleteOrganizationDialogOpen(true)}>Delete</MenuItem>
            </Menu>
        </React.Fragment>
    );
}
